<template>

  <div v-click-outside="onClickOutside"
       ref="host"
       :class="hostClasses">
    <div :class="$style.itemSelect"
         @click="toggleSelect">
      <div :class="$style.configuratorImageWrapper">
        <div :class="$style.orderConfiguratorImage">
          <v-img :src="imagePath"
                 :class="$style.orderConfiguratorImageProduct"
                 aspect-ratio="1"
          />
          <div v-if="selected"
               :class="$style.orderConfiguratorCheckmark">
            <svg v-svg
                 :class="$style.orderCheckmark"
                 symbol="checkmark-icon"/>
          </div>
        </div>
      </div>

      <div :class="$style.orderConfiguratorProperties">
        <div v-if="currentItem.vegan">
          <img alt="Vegan"
               src="@/assets/img/vegan@2x.jpg">
        </div>
        <div v-if="currentItem.glutenFree">
          <img alt="Gluten Free"
               src="@/assets/img/glutenfree@2x.jpg">
        </div>
      </div>

      <div :class="$style.orderConfiguratorName">{{ currentItem.name }}</div>

      <div v-if="shouldShowPrice"
           :class="$style.orderConfiguratorPrice">
        (+{{ price | currency }})
      </div>

      <div :class="$style.optionSubtitle">
        {{ currentItem.subTitle }}
      </div>
    </div>


    <button v-if="isShowModifiers && selected"
            :class="$style.moreInfoBtn"
            @click="showOptions"/>

    <QuantityPicker v-if="showQuantityPicker || isMobilePlatform"
                    :quantity="quantity"
                    :class="[$style.quantityPicker,
                             {[$style.visible]: showQuantityPicker}]"
                    :maximum="currentItem.maxCount"
                    @increaseQuantity="changeQuantity(1)"
                    @decreaseQuantity="changeQuantity(-1)"/>

    <div
      v-if="isShowModifiers"
      :id="optionId"
    >
      <AdditionalOption v-if="itemOptions"
                        :class="$style.additionalOptions"
                        :item-key="itemKey"
                        :checked-modifier-key="checkedModifierKey"
                        @input="setModifier"/>
    </div>

  </div>
</template>

<script>
import { Vue, Component, Ref } from 'vue-property-decorator';
import vClickOutside from 'v-click-outside';
import { Getter } from 'vuex-class';
import scrollIntoView from 'scroll-into-view-if-needed';
import helpers from '@/lib/helpers';
import {
  CategoryWizard,
  ItemImageTypes,
  ItemActionType,
  NO_BURGER_CHEESE,
  NO_BOWL_CHEESE,
  BURGER_DOUBLE_BEEF,
  BOWL_DOUBLE_BEEF,
} from '@/apps/menu/constants';
import QuantityPicker from '@/components/QuantityPicker';
import AdditionalOption from '@/components/AdditionalOption';
import defaultImage from '@/assets/img/no_bundle@3x.svg';
import { CATEGORY_ID } from '@/config/constants';

/**
 * Displays configuration to be selected and modified.
 *
 * Props:
 * @property {any} itemKey - key for item object in the store.
 *
 * Data:
 * @property {boolean} selected - State whenever item is selected or not.
 * @property {number} quantity - Define qty of the selected items. On item
 *                               select will be set to 1. On unselect
 *                               will be set to 0.
 * @property {any} modifier - Key for item modifier. Not set by default.
 *
 */
@Component({
  props: {
    itemKey:     {},
    categoryKey: {},
  },
  components: {
    QuantityPicker,
    AdditionalOption,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
})
export default class ConfiguratorOption extends Vue {
  modifier = null;
  @Getter itemByKey;
  @Getter orderItem;
  @Getter categoryByKey;
  @Getter categories;
  @Getter isMobilePlatform;

  /** @type {boolean} */
  @Getter isFriesDippersCategory;

  @Ref('host') hostElement;

  /* Variable to show or hide additional options */
  itemOptions = false;
  optionId = '';

  /**
   * Provide item object from store based on passed `itemKey` prop.
   * @returns {{
   *   name: string,
   *   orderIndex: number,
   *   maxCount: number,
   *   price: string,
   *   modifiers: [any],
   * }}
   */
  get currentItem() {
    return this.itemByKey(this.itemKey);
  }

  get price() {
    const { productPrice, price, categoryKey } = this.currentItem;

    if ([
      CATEGORY_ID.BURGER_PROTEIN,
      CATEGORY_ID.BOWL_PROTEIN,
    ].includes(categoryKey) && this.quantity === 0) {
      return productPrice;
    }

    return price;
  }

  isProteinCategory(categoryKey) {
    return [ CATEGORY_ID.BURGER_PROTEIN, CATEGORY_ID.BOWL_PROTEIN ]
      .includes(categoryKey);
  }

  /**
   * Calc host classes.
   */
  get hostClasses() {
    return [
      this.$style.orderConfiguratorItem,
      {
        [this.$style.active]:   this.selected,
        [this.$style.disabled]: !this.selected && !this.isAvailable,
      },
    ];
  }

  /**
   * Show quantity picker.
   */
  get showQuantityPicker() {
    return this.selected
      && (!this.isRadio || this.isProteinCategory(this.categoryKey))
      && ![
        CATEGORY_ID.BUNDLE_SAUCES,
        CATEGORY_ID.FRIES_DIPPERS,
        CATEGORY_ID.KIDS_FRIES_DIPPERS,
        CATEGORY_ID.BURGER_SAUCE,
        CATEGORY_ID.BURGER_TOPPINGS,
        CATEGORY_ID.BURGER_PREMIUM_TOPPINGS,
        CATEGORY_ID.BOWL_SAUCE,
        CATEGORY_ID.BOWL_TOPPINGS,
        CATEGORY_ID.BOWL_PREMIUM_TOPPINGS,
        CATEGORY_ID.FRIES_SAUCE,
        CATEGORY_ID.FRIES_TOPPINGS,
        CATEGORY_ID.FRIES_PREMIUM_TOPPINGS,
        CATEGORY_ID.KIDS_BURGER_FRIES,
        CATEGORY_ID.KIDS_BURGER_TOPPINGS,
        CATEGORY_ID.KIDS_BURGER_PREMIUM_TOPPINGS,
      ].includes(this.categoryKey)
      && this.currentItem.maxCount > 1;
  }

  /**
   * Whether configuration option should show price or not.
   * @returns {boolean} Should show price.
   */
  get shouldShowPrice() {
    return this.price > 0 && !this.isFriesDippersCategory(this.categoryKey);
  }

  get checkedModifierKey() {
    let checkedModifier = null;

    if (this.currentItem.modifiers) {
      Object.keys(this.orderItem).forEach((categoryKey) => {
        Object.keys(this.orderItem[categoryKey]).forEach((itemKey) => {
          const currentItemKey = Number(itemKey, 10);
          const item = this.orderItem[categoryKey][itemKey];

          if (currentItemKey === this.itemKey) {
            checkedModifier = item.modifier;
          }
        });
      });
    }

    return checkedModifier;
  }

  /**
   * Check if item is selected in orderItem.
   */
  get selected() {
    const categoryItems = this.orderItem[this.categoryKey];

    if (!categoryItems) {
      return false;
    }

    return Object.prototype.hasOwnProperty.call(
      categoryItems,
      this.itemKey,
    );
  }

  /**
   * Return quantity for item in orderItem
   */
  get quantity() {
    const category = this.orderItem[this.categoryKey];

    if (!category) {
      return 0;
    }

    const item = category[this.itemKey];

    if (!item) {
      return 0;
    }

    return item.quantity;
  }

  /**
   * Check some conditions to display two cheese:
   * current category is cheese, and there no one selected item in this category
   * the double beef is selected as the protein
   * and no cheese is not selected now.
   */
  get addTwoCheeseForBurger() {
    return this.categoryKey === CATEGORY_ID.BURGER_CHEESE
      && this.itemKey !== NO_BURGER_CHEESE
      && !!this.orderItem[CATEGORY_ID.BURGER_PROTEIN][BURGER_DOUBLE_BEEF]
      && (!this.orderItem[CATEGORY_ID.BURGER_CHEESE]
      || Object.keys(this.orderItem[CATEGORY_ID.BURGER_CHEESE]).length === 0
      || Object.keys(this.orderItem[CATEGORY_ID.BURGER_CHEESE])
        .includes(NO_BURGER_CHEESE.toString()));
  }

  /**
   * Check some conditions to display two cheese:
   * current category is cheese, and there no one selected item in this category
   * the double beef is selected as the protein
   * and no cheese is not selected now.
   */
  get addTwoCheeseForBowl() {
    return this.categoryKey === CATEGORY_ID.BOWL_CHEESE
      && this.itemKey !== NO_BOWL_CHEESE
      && !!this.orderItem[CATEGORY_ID.BOWL_PROTEIN][BOWL_DOUBLE_BEEF]
      && (!this.orderItem[CATEGORY_ID.BOWL_CHEESE]
        || Object.keys(this.orderItem[CATEGORY_ID.BOWL_CHEESE]).length === 0
        || Object.keys(this.orderItem[CATEGORY_ID.BOWL_CHEESE])
          .includes(NO_BOWL_CHEESE.toString()));
  }

  /**
   * Selected items count.
   * @return {number}
   */
  get selectedCount() {
    const categoryItems = this.orderItem[this.categoryKey];

    if (!categoryItems) {
      return 0;
    }

    return Object.keys(categoryItems).length;
  }

  /**
   * Is available item or not.
   * @return {boolean}
   */
  get isAvailable() {
    const category = this.categoryKey;
    const categoryItemsLength = this.selectedCount;
    const sauceMaxCount = 10;
    const isNoSelectItem =
      this.currentItem.name.toUpperCase().startsWith('NO ') && this.currentItem.orderIndex === 1;

    if (isNoSelectItem) {
      return true;
    }

    switch (category) {
      /* eslint-disable padding-line-between-statements */
      case CATEGORY_ID.FRIES_DIPPERS:
      case CATEGORY_ID.KIDS_FRIES_DIPPERS:
      case CATEGORY_ID.BUNDLE_SAUCES:
      case CATEGORY_ID.APPETIZER_DIPPERS:
        return categoryItemsLength < sauceMaxCount;
      /* eslint-enable padding-line-between-statements */

      default:
        return true;
    }
  }

  /**
   * Enable or disable item select and emit event to update current order item.
   */
  toggleSelect() {
    if (this.quantity === 0 && this.isAvailable) {
      this.showOptions();
      if (this.addTwoCheeseForBurger || this.addTwoCheeseForBowl) {
        this.emitNewItemData(2);
      } else {
        this.emitNewItemData(1);
      }
    } else {
      this.hideOptions();
      this.emitNewItemData(0);
      this.setModifier(null);
    }
  }

  /* Show additional options */
  showOptions() {
    this.itemOptions = true;

    scrollIntoView(this.hostElement, { behavior: 'smooth', scrollMode: 'if-needed' });
  }

  /* Hide additional options */
  hideOptions() {
    this.itemOptions = false;
  }

  /**
   * Check for hide or show modifiers for category.
   *
   * @return {boolean}
   */
  get isShowModifiers() {
    const { name } = this.itemByKey(this.itemKey);

    if (this.isMobilePlatform) {
      return name.startsWith('Beef')
        && this.hasModifiers;
    }

    return this.hasModifiers;
  }

  /* Hide additional options on click outside the item */
  onClickOutside() {
    this.hideOptions();
  }

  /**
   * Emits data for order item.
   */
  emitNewItemData(quantity) {
    this.$emit('selected', {
      quantity,
      itemKey:  this.itemKey,
      modifier: this.modifier,
      ...this.currentItem,
    });
  }

  /**
   * Retrieve image url for item
   */
  get imagePath() {
    if (this.currentItem.images && this.menuImage) {
      return helpers.getImagePath(this.menuImage.url);
    }

    return defaultImage;
  }

  /**
   * Get menu image for item.
   */
  get menuImage() {
    return this.currentItem.images
      .find(image =>
        image.type === ItemImageTypes.configuratorMenuImage);
  }

  /**
   * Change quantity data and emit updated order item.
   *
   * @param {number} diff - amount for quantityChange.
   */
  changeQuantity(diff) {
    const newQuantity = this.quantity + diff;

    if (newQuantity <= 0) {
      this.toggleSelect();
    } else {
      this.emitNewItemData(newQuantity);
    }
  }

  /**
   * Check item's wizard category is of type radio
   */
  get isRadio() {
    const { wizardType } = this.categoryByKey(this.categoryKey);

    return (
      wizardType === CategoryWizard.RADIO ||
      this.currentItem.actionType === ItemActionType.NO_CHOICE
    );
  }

  /**
   * Define if modifiers switch will be shown
   */
  get hasModifiers() {
    // Hide sauces for mobile platform
    if (this.isMobilePlatform) {
      if ([
        CATEGORY_ID.BOSS_FRIES,
        CATEGORY_ID.FRIES_CHEESE,
        CATEGORY_ID.FRIES_SAUCE,
        CATEGORY_ID.FRIES_SAUCE,
        CATEGORY_ID.BURGER_SAUCE,
        CATEGORY_ID.KIDS_BURGER_SAUCE,
        CATEGORY_ID.BOWL_SAUCE,
      ].includes(this.currentItem.categoryKey)) return false;
    }

    return this.currentItem.modifiers.length !== 0;
  }

  /**
   * Set new modifier value and emit updated order item.
   * @param modifier
   */
  setModifier(modifier) {
    this.modifier = modifier;
    this.emitNewItemData(this.quantity);
  }

  /**
   * Set Option ID
   */
  setOptionId() {
    this.optionId = `additional-option-${Math.random().toString(36).substr(2, 5)}`;
  }

  mounted() {
    this.setOptionId();
  }
}

</script>

<style lang="scss" module>
@import "~@/style/variables.scss";

.orderConfiguratorItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 23px;
  position: relative;
  padding: 0 15px;

  > .itemSelect {
    width: 100%;
  }

  &.disabled {
    opacity: 0.5;
  }
}

.configuratorImageWrapper {
  position: relative;
}

.orderConfiguratorImage {
  width: 84px;
  margin: 0 auto;
  border-radius: 50%;
  border: 4px solid transparent;
  transition: border-color 0.25s ease-in-out;
  position: relative;
  z-index: 2;
}

.orderConfiguratorImageProduct {
  border-radius: 50%;
}

.orderConfiguratorItem.active .orderConfiguratorImage {
  border-color: $green-color;
}

.orderConfiguratorProperties {
  display: flex;
  justify-content: center;
  height: 24px;
  margin-top: 3px;

  &+& {
    margin-left: 3px;
  }

  img {
    width: 24px;
    height: 24px;
  }
}

.orderConfiguratorName {
  font-family: 'Gotham', sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 300;
  color: $black;
  text-align: center;
}

.orderConfiguratorPrice, .optionSubtitle {
  font-family: 'Gotham', sans-serif;
  text-align: center;
  font-weight: 300;
}

.moreInfoBtn {
  background-image: url("/static/more.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 16px;
  width: 100%;
  height: 20px;
  outline: none;
}

.orderConfiguratorCheckmark {
  position: absolute;
  right: -25px;
  top: 0;
  background-color: $green-color;
  border-radius: 50%;
  width: 23px;
  height: 23px;

  .orderCheckmark {
    width: 23px;
    height: 23px;
    fill: $white;
  }
}

.quantityPicker {
  visibility: hidden;

  &.visible {
    visibility: visible;
  }
}

@media screen and (max-width: $kiosk-breakpoint-max) {
  .orderConfiguratorItem {
    margin-bottom: 42px;
    position: static;
  }

  .orderConfiguratorImage {
    width: 129px;
    border: 7px solid transparent;
  }

  .orderConfiguratorProperties {
    height: 38px;

    img {
      width: 38px;
      height: 38px;
    }
  }

  .orderConfiguratorName {
    font-size: 24px;
    line-height: 31px;
    margin-top: 10px;
  }

  .moreInfoBtn {
    background-size: 43px;
  }

  .orderConfiguratorCheckmark {
    width: 38px;
    height: 38px;
    right: -43px;

    .orderCheckmark {
      width: 38px;
      height: 38px;
    }
  }
}

@media screen and (max-width: $mobile-breakpoint-max) {
  .orderConfiguratorItem {
    margin-bottom: 10px;
  }

  .configuratorImageWrapper {
    max-width: 67px;
    margin: auto;
  }

  .orderConfiguratorImage {
    width: 100%;
    border: 3px solid transparent;
  }

  .orderConfiguratorProperties {
    height: 19px;

    img {
      width: 19px;
      height: 19px;
    }
  }

  .orderConfiguratorName {
    font-size: 12px;
    line-height: 14px;
    margin-top: 0;
  }

  .orderConfiguratorPrice, .optionSubtitle {
    font-size: 12px;
  }

  .moreInfoBtn {
    background-size: 16px;
  }

  .orderConfiguratorCheckmark {
    width: 18px;
    height: 18px;
    right: -20px;

    .orderCheckmark {
      width: 18px;
      height: 18px;
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/apps/configurator/components/ConfiguratorOption.vue